import type { PumpStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import { PumpAlgorithm } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ModalBody,
  ModalCloseX,
  ModalContent,
  ModalHeading,
  InsulinOutlineIcon,
} from "@tandemdiabetes/sugarmate-react-components"
import { useMemo } from "react"

import { ControlIQModeRow } from "./ControlIQModeRow"
import styles from "./PumpStatusModalContent.module.scss"

import {
  PumpAlgorithmIcon,
  PumpAlgorithmStatus,
  PumpBasalRate,
  PumpBasalRateState,
  PumpBasalRateIcon,
  PumpBatteryIcon,
  PumpBatteryPercentage,
  PumpCartridgeFill,
  PumpCartridgeIcon,
  PumpInsulinOnBoard,
  PumpSensorExpirationDate,
  PumpSensorExpirationIcon,
  PumpSensorExpirationTime,
  UserData,
  UserDataSynced,
} from "src/components"
import { usePolyglot } from "src/contexts"
import {
  usePumpStatusRecency,
  useIsPumpStatusSensorExpirationStale,
} from "src/hooks"
import { DataRecency } from "src/types"

const polyglotPrefix = "pages.home.pump_status_modal."

interface Props {
  pumpStatus: PumpStatus;
}

/*
 * this component renders the modal content of a table of pump status info
 */
export function PumpStatusModalContent(props: Props) {
  const { pumpStatus } = props

  const polyglot = usePolyglot()
  const {
    pump_algorithm: pumpAlgorithm,
    last_pump_event_at: syncedAt,
    sensor_expiration: sensorExpiration,
  } = pumpStatus

  const pumpStatusRecency = usePumpStatusRecency(pumpStatus)
  const isSensorExpirationStale = useIsPumpStatusSensorExpirationStale(pumpStatus)

  const showExpirationTime = sensorExpiration !== null && !isSensorExpirationStale

  const pumpAlgorithmKey = useMemo(
    () => {
      switch (pumpAlgorithm) {
        case PumpAlgorithm.ControlIQ:
          return `${polyglotPrefix}algorithm.control_iq`
        case PumpAlgorithm.BasalIQ:
          return `${polyglotPrefix}algorithm.basal_iq`
        case PumpAlgorithm.NoData:
        default:
          return `${polyglotPrefix}algorithm.unknown`
      }
    },
    [pumpAlgorithm],
  )

  return (
    <ModalContent>
      <ModalHeading>
        <div className={styles.headingTextContainer}>
          <UserData pumpStatus={pumpStatus} />
          <UserDataSynced
            className={styles.syncedAt}
            syncedAt={syncedAt}
          />
        </div>
        <ModalCloseX id="pump-status-modal-close-btn" />
      </ModalHeading>
      <ModalBody
        className={
          pumpStatusRecency === DataRecency.Current
            ? styles.bodyCurrent
            : styles.bodyNotCurrent
        }
      >
        <div className={styles.singleRowSection}>
          <PumpBasalRateIcon
            pumpStatus={pumpStatus}
            pumpStatusRecency={pumpStatusRecency}
            className={styles.icon}
          />
          <PumpBasalRateState
            pumpStatus={pumpStatus}
            pumpStatusRecency={pumpStatusRecency}
            className={styles.sectionTitle}
          />
          <PumpBasalRate
            pumpStatus={pumpStatus}
            pumpStatusRecency={pumpStatusRecency}
            valueClassName={styles.basalRateValue}
          />
        </div>
        <div className={styles.multiRowSection}>
          <div className={styles.sectionRow}>
            <PumpAlgorithmIcon
              pumpStatus={pumpStatus}
              pumpStatusRecency={pumpStatusRecency}
              className={styles.icon}
            />
            <div className={styles.sectionTitle}>
              {polyglot.t(pumpAlgorithmKey)}
            </div>
            <PumpAlgorithmStatus
              pumpStatus={pumpStatus}
              pumpStatusRecency={pumpStatusRecency}
            />
          </div>
          <ControlIQModeRow
            pumpStatus={pumpStatus}
            pumpStatusRecency={pumpStatusRecency}
          />
        </div>
        {pumpAlgorithm === PumpAlgorithm.ControlIQ && (
          // this intentionally only renders Insulin On Board for ControlIQ Algorithm
          <div className={styles.singleRowSection}>
            <InsulinOutlineIcon className={styles.icon} />
            <div className={styles.sectionTitle}>
              {polyglot.t(`${polyglotPrefix}iob`)}
            </div>
            <PumpInsulinOnBoard
              pumpStatus={pumpStatus}
              pumpStatusRecency={pumpStatusRecency}
              className={styles.insulinOnBoard}
            />
          </div>
        )}
        <div className={styles.singleRowSection}>
          <PumpBatteryIcon
            pumpStatus={pumpStatus}
            pumpStatusRecency={pumpStatusRecency}
            className={styles.icon}
          />
          <div className={styles.sectionTitle}>
            {polyglot.t(`${polyglotPrefix}pump_battery`)}
          </div>
          <PumpBatteryPercentage
            pumpStatus={pumpStatus}
            pumpStatusRecency={pumpStatusRecency}
            className={styles.pumpBattery}
          />
        </div>
        <div className={styles.singleRowSection}>
          <PumpCartridgeIcon
            pumpStatus={pumpStatus}
            pumpStatusRecency={pumpStatusRecency}
            className={styles.icon}
          />
          <div className={styles.sectionTitle}>
            {polyglot.t(`${polyglotPrefix}insulin_left`)}
          </div>
          <PumpCartridgeFill
            pumpStatus={pumpStatus}
            pumpStatusRecency={pumpStatusRecency}
            className={styles.cartridgeFill}
          />
        </div>
        <div className={styles.multiRowSection}>
          <div className={styles.sectionRow}>
            <PumpSensorExpirationIcon
              pumpStatus={pumpStatus}
              isSensorExpirationStale={isSensorExpirationStale}
              className={styles.icon}
            />
            <div className={styles.sectionTitle}>
              {polyglot.t(`${polyglotPrefix}sensor_expiration`)}
            </div>
            <PumpSensorExpirationDate
              pumpStatus={pumpStatus}
              isSensorExpirationStale={isSensorExpirationStale}
              className={styles.sensorExpirationDate}
            />
          </div>
          {showExpirationTime && (
            <div className={styles.sectionRowWithIndent}>
              <PumpSensorExpirationTime
                pumpStatus={pumpStatus}
                isSensorExpirationStale={isSensorExpirationStale}
                className={styles.sensorExpirationTime}
              />
            </div>
          )}
        </div>
      </ModalBody>
    </ModalContent>
  )
}
